import React, { useState, useEffect } from 'react';
import { Select, MenuItem, CircularProgress, Typography, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { getEffectiveValue } from '../../utils/receiptsUtils';
import { useNavigate } from 'react-router-dom';

function DiscountsThisMonth({ 
  selectedMonth, 
  selectedYear, 
  receipts,
  handleMonthChange,
  formatDate,
}) {
  const navigate = useNavigate();
  const [totalDiscounts, setTotalDiscounts] = useState(0);
  const [discountDetails, setDiscountDetails] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [monthOptions, setMonthOptions] = useState([]);

  // Generate month options for the last 12 months plus current month
  useEffect(() => {
    const generateMonthOptions = () => {
      const options = [];
      const currentDate = new Date();
      const currentMonth = currentDate.getMonth();
      const currentYear = currentDate.getFullYear();

      // Add current month and previous 12 months
      for (let i = 0; i < 13; i++) {
        let month = currentMonth - i;
        let year = currentYear;

        if (month < 0) {
          month = 12 + month;
          year = year - 1;
        }

        options.push({
          month: month,
          year: year,
          label: new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' }).format(new Date(year, month))
        });
      }
      setMonthOptions(options);

      // Initialize to the most recent month
      if (options.length > 0) {
        const mostRecent = options[0];
        handleMonthChange(mostRecent.month, mostRecent.year);
      }
    };

    generateMonthOptions();
  }, []);

  useEffect(() => {
    setIsLoading(true);
    processReceiptData(receipts).finally(() => {
      setIsLoading(false);
    });
  }, [receipts, selectedMonth, selectedYear]);

  const processReceiptData = async (receipts) => {
    try {
      const thisMonthReceipts = receipts.filter(receipt => {
        const receiptDate = new Date(getEffectiveValue(receipt, 'date') || getEffectiveValue(receipt, 'receipt_date'));
        return receiptDate.getMonth() === selectedMonth && receiptDate.getFullYear() === selectedYear;
      });

      // Fetch items for each receipt
      const allItems = await Promise.all(thisMonthReceipts.map(async (receipt) => {
        try {
          const response = await axios.get(`${API_BASE_URL}/get_receipt_items?receipt_id=${receipt.uuid}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });
          // Add receipt date, merchant name, and receipt ID to each item
          return response.data ? response.data.map(item => ({
            ...item,
            date: getEffectiveValue(receipt, 'date') || getEffectiveValue(receipt, 'receipt_date'),
            merchant_name: getEffectiveValue(receipt, 'merchant_name') || getEffectiveValue(receipt, 'store_name') || 'Unknown',
            receipt_id: receipt.uuid
          })) : [];
        } catch (error) {
          console.error('Error fetching items for receipt:', receipt.uuid, error);
          return [];
        }
      }));

      // Filter out any null or undefined results and flatten the array
      const flattenedItems = allItems.filter(items => Array.isArray(items)).flat();
      
      // Process items to find discounts
      const discounts = flattenedItems.filter(item => {
        const price = parseFloat(getEffectiveValue(item, 'item_price')) || 0;
        return price < 0 || item.is_maybe_discount; // Consider both negative prices and is_maybe_discount flag
      });

      const total = discounts.reduce((sum, item) => 
        sum + Math.abs(parseFloat(getEffectiveValue(item, 'item_price')) || 0), 0);
      
      setTotalDiscounts(total);
      setDiscountDetails(discounts.map(item => ({
        itemName: getEffectiveValue(item, 'item_name'),
        amount: Math.abs(parseFloat(getEffectiveValue(item, 'item_price')) || 0),
        vendor: item.merchant_name,
        date: item.date,
        receipt_id: item.receipt_id
      })));

    } catch (error) {
      console.error('Error processing receipt data:', error);
    }
  };

  const handleDiscountClick = (receipt_id) => {
    if (receipt_id) {
      navigate(`/receipts/${receipt_id}`);
    }
  };

  return (
    <div>
      <div className="mb-4">
        <Select
          value={selectedMonth !== undefined && selectedYear !== undefined ? 
            JSON.stringify({
              month: selectedMonth,
              year: selectedYear,
              label: new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' }).format(new Date(selectedYear, selectedMonth))
            }) : monthOptions.length > 0 ? JSON.stringify(monthOptions[0]) : ''}
          onChange={(e) => {
            const selectedOption = JSON.parse(e.target.value);
            handleMonthChange(selectedOption.month, selectedOption.year);
          }}
          style={{
            color: 'white',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            height: '32px',
            fontSize: '0.875rem',
          }}
          fullWidth
          MenuProps={{
            PaperProps: {
              style: {
                backgroundColor: '#1F2937',
                color: 'white',
              },
            },
          }}
        >
          {monthOptions.map((option, index) => (
            <MenuItem 
              key={index} 
              value={JSON.stringify(option)}
            >
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </div>

      <div className="flex flex-col items-center justify-center">
        <div className="text-4xl font-bold mb-2 bg-clip-text text-transparent bg-gradient-to-r from-green-400 to-emerald-600">
          ${totalDiscounts.toFixed(2)}
        </div>
        <div className="text-gray-400 text-sm mb-4">Total Discounts Earned</div>
      </div>

      {isLoading ? (
        <div className="flex justify-center">
          <CircularProgress />
        </div>
      ) : discountDetails.length > 0 ? (
        <Accordion 
          sx={{ 
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            color: 'white',
            '&:before': {
              display: 'none',
            }
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
          >
            <Typography>View Discount Details</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <div className="space-y-2">
              {discountDetails.map((discount, index) => (
                <div 
                  key={index} 
                  className="p-2 rounded bg-gray-700 flex justify-between items-center hover:bg-gray-600 cursor-pointer transition-colors duration-200"
                  onClick={() => handleDiscountClick(discount.receipt_id)}
                >
                  <div>
                    <div className="font-medium">{discount.itemName}</div>
                    <div className="text-sm text-gray-400">
                      {discount.vendor} • {new Date(discount.date).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="text-green-400 font-medium">
                    -${discount.amount.toFixed(2)}
                  </div>
                </div>
              ))}
            </div>
          </AccordionDetails>
        </Accordion>
      ) : (
        <div className="text-center text-gray-400">
          No discounts found for this month
        </div>
      )}
    </div>
  );
}

export default DiscountsThisMonth; 